import React, { useMemo } from 'react';
import { Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { GridColDef } from '@mui/x-data-grid';
import { DataGridPro, GridSortItem } from '@mui/x-data-grid-pro';
import { PagedList } from '../../data/models/paged-list.model';
import { BaseProvider, Tier3Provider } from '../../data/models/provider.model';
import { Driver } from '../../data/models/driver.model';
import { Vehicle } from '../../data/models/vehicle.model';
import { User } from '../../data/models/user.model';
import { SortModel } from '../../data/models/sort-model.model';
import { buildColumnVisibilityModel } from '../../services/dataGridUtils';

const PREFIX = 'DataTable';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    justifyContent: 'stretch',
  },

  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-cell': {
      display: 'flex',
      alignItems: 'center',
    },
  },
}));

interface DataTableProps {
  columns: GridColDef[];
  itemList: PagedList<BaseProvider | Driver | Vehicle | User | Tier3Provider>;
  pageSize: number;
  sortModel: SortModel | null;
  loading: boolean;
  rowSelection?: boolean;
  getRowId: (row: any) => number;
  onPageChange: (val: number) => void;
  onPageSizeChange: (val: number) => void;
  onSortModelChange: (val: SortModel | null) => void;
}

export default function DataTable({
  columns,
  itemList,
  pageSize,
  sortModel,
  loading,
  rowSelection,
  getRowId,
  onPageChange,
  onPageSizeChange,
  onSortModelChange,
}: DataTableProps) {
  const mappedSortModel: GridSortItem[] = sortModel
    ? [
        {
          field: sortModel.column,
          sort: sortModel.direction,
        },
      ]
    : [];

  const sortModelChange = (sortModel: GridSortItem[]) => {
    const newModel = sortModel.length
      ? ({
          column: sortModel[0].field,
          direction: sortModel[0].sort,
        } as SortModel)
      : null;
    onSortModelChange(newModel);
  };

  const paginationModel = {
    page: itemList.currentPage - 1,
    pageSize: pageSize,
  };

  const columnVisibilityModel = useMemo(
    () => buildColumnVisibilityModel(columns as any),
    [columns],
  );

  return (
    <StyledPaper className={classes.container}>
      <DataGridPro
        getRowId={getRowId}
        rows={itemList.items}
        columns={columns as any}
        paginationModel={paginationModel}
        rowCount={itemList.itemCount}
        autoHeight={true}
        loading={loading}
        paginationMode="server"
        sortingMode="server"
        className={classes.grid}
        rowSelection={rowSelection ?? true}
        checkboxSelection={false}
        disableColumnMenu={true}
        disableColumnSelector={true}
        sortModel={mappedSortModel}
        columnVisibilityModel={columnVisibilityModel}
        pageSizeOptions={[10, 20, 30, 40, 50, 100]}
        getRowHeight={() => 'auto'}
        onPaginationModelChange={(model) => {
          if (model.page !== paginationModel.page) {
            onPageChange(model.page + 1);
          }
          if (model.pageSize !== paginationModel.pageSize) {
            onPageSizeChange(model.pageSize);
          }
        }}
        onSortModelChange={sortModelChange}
        pagination={true}
      />
    </StyledPaper>
  );
}
